import React, {useState, useEffect, useRef} from 'react';
import {Form, Dropdown, Button, Message} from 'semantic-ui-react';
import {AddButton} from '../../components/AddButton/AddButton';
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {SemanticToastContainer, toast} from 'react-semantic-toasts';
import {useCustomers} from '../../providers/CustomerProvider';
import {useUtilityAccounts} from '../../providers/UtilityAccountProvider';
import {UtilityAccountModal} from './UtilityAccountModals/UtilityAccountModal';
import {UtilityRatesModal} from './UtilityAccountModals/UtilityRatesModal';
import './UtilityAccountModals/UtilityAccounts.css';
import {useAuth} from "../../providers/AuthenticationProvider";
import {useNavigate} from "react-router";
import {DeleteConfirmation} from "./UtilityAccountModals/DeleteConfirmation";
const WEB_PAGE = 'Utility Accounts';

const toSortParam = sortState => {
  const s = sortState[0] ?? { field: 'name', dir: 'asc' };
  return [{ fieldName: s.field, dir: s.dir }];
};

const inputRow = {
  width: '100%',
  display: 'flex',
  alignItems: 'flex-end',
  marginTop: '1rem',
};


const addDecimals = num => {
  return Number.parseFloat(num).toFixed(2);
};

export const UtilityAccounts = () => {
  const { checkAuth, getUserType } = useAuth();
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortState, setSortState] = useState([{field: 'name', dir: 'desc'}]);
  const [customer, setCustomer] = useState();
  const [selectedUtilityAccount, setSelectedUtilityAccount] = useState();
  const [open, setOpen] = useState(false);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [ratesHasChanges, setRatesHasChanges] = useState(false);
  const wasRatesOpen = useRef(false);
  const [deleteOpen, setDeleteOpen] =useState(false);
  const [openType, setOpenType] = useState('Create'); // Create or Edit

  const {customersList, getAllCustomers, selectedCustomer, setSelectedCustomer} = useCustomers();
  const {total, pageCount, getUtilityAccounts, deleteUtilityAccount, utilityAccounts} =
    useUtilityAccounts();
  const navigate = useNavigate();

  checkAuth(getUserType(), WEB_PAGE) && navigate('/');

  useEffect(() => {
    getAllCustomers();
  }, [getAllCustomers]);

  useEffect(() => {
    if (!selectedCustomer || !customersList.length || customer) return;
    const restored = customersList.find(x => x.id === selectedCustomer);
    if (restored) setCustomer(restored);
  }, [customersList, selectedCustomer]);

  useEffect(() => {
    if (!customer) return;
    if (!customer.groupId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getUtilityAccounts(toSortParam(sortState), page, pageSize, customer.groupId)
        .then(() => {setLoading(false)})
        .catch(() => {setLoading(false)});
  }, [sortState, page, pageSize, getUtilityAccounts, customer]);
  
  useEffect(() => {
    if (wasRatesOpen.current && !ratesOpen && ratesHasChanges && customer && customer.groupId) {
      setLoading(true);
      getUtilityAccounts(toSortParam(sortState), page, pageSize, customer.groupId)
          .then(() => setLoading(false))
          .catch(() => {setLoading(false)});
    }

    // Prevent re-fetching on initial render
    wasRatesOpen.current = ratesOpen;
  }, [ratesOpen, ratesHasChanges, sortState, page, pageSize, getUtilityAccounts, customer]);

  const handleEdit = utilityAccount => {
    setOpenType('Edit');
    setSelectedUtilityAccount(utilityAccount);
    setOpen(true);
  };

  const handleRatesClick = utilityAccount => {
    setSelectedUtilityAccount(utilityAccount);
    setRatesOpen(true);
  };

  const handleViewMeters = utilityAccount => {
    navigate(`/utility-account/${utilityAccount.id}/utility-meters`, { state: { accountName: utilityAccount.name } });
  };

  const handleDelete = utilityAccount => {
    setSelectedUtilityAccount(utilityAccount);
    setDeleteOpen(true);
  };
  
  const deleteAccount = async (utilityAccount) => {
    setLoading(true);
    try {
      await deleteUtilityAccount(utilityAccount.id);
      
      await getUtilityAccounts(toSortParam(sortState), page, pageSize, customer?.groupId);
      
      toast({
        type: 'success',
        title: 'Success',
        description: 'Utility account deleted successfully',
        animation: 'fade down',
        time: 4000
      });
    } catch (error) {
      console.error("Error deleting utility account: ", error);
      toast({
        type: 'error',
        title: 'Error',
        description: 'An error occurred while deleted the utility account.',
        animation: 'fade down',
        time: 8000
      });
    }
    
    setDeleteOpen(false);
    setSelectedUtilityAccount();
    setLoading(false);
  }

  const handleCreateAccount = () => {
    if (!customer) {
      alert('Please select a customer');
      return;
    }
    setSelectedUtilityAccount({ utilityRates: [] });
    setOpenType('Create');
    setOpen(true);
  };

  return (
    <>
      <UtilityAccountModal
        open={open}
        setOpen={setOpen}
        type={openType}
        customer={customer}
        selectedUtilityAccount={selectedUtilityAccount}
        setLoading={setLoading}
        onSuccess={() => getUtilityAccounts(toSortParam(sortState), page, pageSize, customer?.groupId)}
      />
      <UtilityRatesModal
        ratesOpen={ratesOpen}
        setRatesOpen={setRatesOpen}
        setRatesHasChanges={setRatesHasChanges}
        selectedUtilityAccount={selectedUtilityAccount}
      />
      <DeleteConfirmation
        open={deleteOpen}
        setOpen={setDeleteOpen}
        submitAction={() => deleteAccount(selectedUtilityAccount)}
        loading={loading}
        entityName="utility account"
      />
      <div>
        <div className="pageHeader">
          <h1>Utility Accounts</h1>
          <div className="utility-accounts-create">
            <Form>
              <Form.Group style={{flexDirection: 'column'}}>
                <div style={inputRow}>
                  <Dropdown
                    className="create-dropdown"
                    label="Customer"
                    placeholder="Select a Customer"
                    value={customer?.id}
                    options={customersList.map(x => {
                      const text =
                        x?.firstname && x?.lastname
                          ? `${x?.companyName} (${x?.firstname} ${x?.lastname})`
                          : x?.companyName;
                      return {
                        key: x?.id,
                        text: text,
                        value: x?.id,
                      };
                    })}
                    onChange={(e, selected) => {
                      const found = customersList.find(x => x.id === selected.value);
                      setCustomer(found);
                      setSelectedCustomer(found?.id ?? null);
                      setPage(0);
                    }}
                    selection
                    search
                  />
                  <AddButton disabled={!customer || !customer.groupId} onClick={() => handleCreateAccount()}>Create Account</AddButton>
                </div>
              </Form.Group>
            </Form>
          </div>
        </div>
        {!customer && (
          <div style={{ textAlign: 'center', marginTop: '3rem', color: '#999', fontSize: '1.1rem' }}>
            Select a customer to view utility accounts
          </div>
        )}
        {customer && !customer.groupId && (
          <Message warning>
            <Message.Header>No Group ID assigned</Message.Header>
            <p>This customer does not have a Group ID. Utility accounts cannot be loaded until a Group ID is set on the customer.</p>
          </Message>
        )}
        {customer && customer.groupId && utilityAccounts && (
          <div style={{ position: 'relative' }}>
            <Grid
              data={utilityAccounts}
              sortable={true}
              sort={sortState}
              onSortChange={e => setSortState(e.sort)}
              pageable={{ pageSizes: [5, 10, 20] }}
              skip={page * pageSize}
              take={pageSize}
              total={total}
              onPageChange={e => {
                setPage(e.page.skip / e.page.take);
                setPageSize(e.page.take);
              }}
            >
              <Column field="name" title="Name" />
              <Column field="num" title="Equipment Num" cell={props => (
                <td>{props.dataItem.num ?? ''}</td>
              )} />
              <Column field="type" title="Type" />
              <Column title="Current Utility Rate" sortable={false} cell={props => (
                <td>
                  <button
                    className={'utilityRateButton'}
                    onClick={() => handleRatesClick(props.dataItem)}
                  >
                    {props.dataItem.utilityRates[0]
                      ? '$' + addDecimals(props.dataItem.utilityRates[0].utility_rate)
                      : 'N/A'}
                    {props.dataItem.utilityRates[0]?.utility_rate_unit
                      ? ' ' + props.dataItem.utilityRates[0].utility_rate_unit
                      : ''}
                  </button>
                </td>
              )} />
              <Column  width="325px" sortable={false} cell={props => (
                <td>
                  <Button onClick={() => handleEdit(props.dataItem)}>
                    Edit
                  </Button>
                  <Button onClick={() => handleDelete(props.dataItem)}>
                    Delete
                  </Button>
                  <Button onClick={() => handleViewMeters(props.dataItem)}>
                    View Meters
                  </Button>
                </td>
              )} />
            </Grid>
          </div>
        )}
        <SemanticToastContainer position="bottom-center" />
      </div>
    </>
  );
};
